// Pure helpers behind Settings › « Fournisseurs IA » and « Comparer les modèles »
// (owner, 2026-10-23). Everything that can be WRONG rather than merely ugly —
// money formatting, the route ⇄ dropdown mapping, the face-box maths, the run
// totals — lives here so it is tested without rendering a page.
import type {
  AiProvidersState, CompareCase, CompareHop, CompareRun, FaceBox, ProviderId, ReplyRules, TaskRoute, TaskRow,
} from './aiProvidersApi';

// ─── money ───────────────────────────────────────────────────────────────────

/** `$12.34`; small amounts keep more digits so a 0.004 $ call is not « $0.00 ». */
export function fmtUsd(n: number | null | undefined, digits?: number): string {
  if (n == null || !Number.isFinite(n)) return '—';
  const d = digits ?? (Math.abs(n) > 0 && Math.abs(n) < 1 ? 3 : 2);
  return `$${n.toFixed(d)}`;
}

/** USD → MAD at the app's rate, rounded to the dirham (≥ 10) or to 0.1. */
export function usdToMad(usd: number | null | undefined, fxUsd: number): number | null {
  if (usd == null || !Number.isFinite(usd) || !Number.isFinite(fxUsd) || fxUsd <= 0) return null;
  const mad = usd * fxUsd;
  return Math.abs(mad) >= 10 ? Math.round(mad) : Math.round(mad * 10) / 10;
}

export function fmtMad(usd: number | null | undefined, fxUsd: number): string {
  const mad = usdToMad(usd, fxUsd);
  return mad == null ? '—' : `${mad.toLocaleString('fr-FR')} DH`;
}

export type Tone = 'ok' | 'amber' | 'red';

/** Spend vs budget: amber from 80 %, red from 100 %. No budget → no bar (null). */
export function budgetBar(spent: number, budget: number | null | undefined): { pct: number; tone: Tone } | null {
  if (budget == null || !Number.isFinite(budget) || budget <= 0) return null;
  const pct = Math.max(0, (spent / budget) * 100);
  return { pct, tone: pct >= 100 ? 'red' : pct >= 80 ? 'amber' : 'ok' };
}

/** A number field's text → number | null (empty = null; « 1,5 » accepted). */
export function parseAmount(s: string): number | null {
  const t = s.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** YYYY-MM-DD → DD/MM/YYYY. */
export const frDay = (d: string | null | undefined) => (d ? d.slice(0, 10).split('-').reverse().join('/') : '—');

// ─── task routes ⇄ dropdowns ─────────────────────────────────────────────────
// An option is « provider|model »; model '' = null (= OpenRouter « actuel »,
// what the code / settings ask for today). The fallback's « none » is ''.

export const NO_FALLBACK = '';
export const OR_CURRENT = 'openrouter|';

export function hopKey(provider: ProviderId | null, model: string | null): string {
  return provider ? `${provider}|${model ?? ''}` : NO_FALLBACK;
}

export function parseHopKey(key: string): { provider: ProviderId; model: string | null } | null {
  if (!key) return null;
  const i = key.indexOf('|');
  const provider = key.slice(0, i) as ProviderId;
  if (provider !== 'openrouter' && provider !== 'anthropic') return null;
  const model = key.slice(i + 1);
  return { provider, model: model || null };
}

export interface RouteForm { primary: string; fallback: string; imageLongSide: string }

export const DEFAULT_FORM: RouteForm = { primary: OR_CURRENT, fallback: NO_FALLBACK, imageLongSide: '' };

/** What the row's dropdowns show. No route = « OpenRouter — actuel », no fallback. */
export function routeToForm(route: TaskRoute | null): RouteForm {
  if (!route) return { ...DEFAULT_FORM };
  return {
    primary: hopKey(route.primaryProvider, route.primaryModel),
    fallback: hopKey(route.fallbackProvider, route.fallbackModel),
    imageLongSide: route.imageLongSide == null ? '' : String(route.imageLongSide),
  };
}

/** Choosing Claude as the primary pre-fills the fallback with OpenRouter « actuel ». */
export function withPrimary(form: RouteForm, primary: string): RouteForm {
  const p = parseHopKey(primary);
  const fallback = p?.provider === 'anthropic' && form.fallback === NO_FALLBACK ? OR_CURRENT : form.fallback;
  return { ...form, primary, fallback: fallback === primary ? NO_FALLBACK : fallback };
}

/** The form as the worker's TaskRoute. Image size only for vision tasks (blank = default 768). */
export function formToRoute(form: RouteForm, row: Pick<TaskRow, 'vision'>): TaskRoute {
  const p = parseHopKey(form.primary) ?? { provider: 'openrouter' as const, model: null };
  const f = parseHopKey(form.fallback);
  const side = row.vision ? parseInt(form.imageLongSide, 10) : NaN;
  return {
    primaryProvider: p.provider,
    primaryModel: p.model,
    fallbackProvider: f?.provider ?? null,
    fallbackModel: f?.model ?? null,
    imageLongSide: Number.isFinite(side) && side > 0 ? side : null,
  };
}

/** The form says exactly the shipped default — saving it means « reset », not a stored route. */
export function isDefaultForm(form: RouteForm): boolean {
  return form.primary === OR_CURRENT && form.fallback === NO_FALLBACK && !form.imageLongSide.trim();
}

export function sameForm(a: RouteForm, b: RouteForm): boolean {
  return a.primary === b.primary && a.fallback === b.fallback && a.imageLongSide.trim() === b.imageLongSide.trim();
}

export interface Option { value: string; label: string }

/** Same shape as useT()'s t — the helpers stay pure and still translate. */
export type Tr = (fr: string, vars?: Record<string, string | number>) => string;
export const idTr: Tr = (fr, vars) => {
  let s = fr;
  if (vars) for (const k of Object.keys(vars)) s = s.split(`{${k}}`).join(String(vars[k]));
  return s;
};

/** Dropdown options for a task. Voice (audio) = OpenRouter only. */
export function routeOptions(
  row: Pick<TaskRow, 'currentModel' | 'audio'>,
  models: AiProvidersState['models'],
  which: 'primary' | 'fallback',
  t: Tr = idTr,
): Option[] {
  const out: Option[] = [];
  if (which === 'fallback') out.push({ value: NO_FALLBACK, label: t('Aucun') });
  const m = row.currentModel || '—';
  out.push({ value: OR_CURRENT, label: which === 'primary' ? t('OpenRouter — actuel : {m}', { m }) : t('OpenRouter — actuel ({m})', { m }) });
  for (const o of models.openrouter) if (o !== row.currentModel) out.push({ value: hopKey('openrouter', o), label: `OpenRouter — ${o}` });
  if (!row.audio) for (const a of models.anthropic) out.push({ value: hopKey('anthropic', a.id), label: a.label });
  return out;
}

// ─── WhatsApp easy / complex rules ───────────────────────────────────────────

export type RuleListKey = 'money_words' | 'complaint_words' | 'edit_words' | 'easy_words' | 'darija_markers';
export const RULE_LISTS: RuleListKey[] = ['money_words', 'complaint_words', 'edit_words', 'easy_words', 'darija_markers'];

/** Comma / new-line separated words → a clean list (trimmed, lower-case, no duplicates). */
export function splitWords(text: string): string[] {
  const seen = new Set<string>();
  for (const w of text.split(/[,\n]/)) {
    const t = w.trim().toLowerCase();
    if (t) seen.add(t);
  }
  return [...seen];
}

export const joinWords = (list: string[]) => list.join(', ');

/** The rules form's numbers, clamped to what the worker accepts. */
export function clampRules(r: ReplyRules): ReplyRules {
  const c = (v: number, lo: number, hi: number, d: number) => (Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : d);
  return {
    ...r,
    max_easy_chars: c(r.max_easy_chars, 10, 2000, 160),
    max_easy_questions: c(r.max_easy_questions, 0, 10, 1),
    sticky_minutes: c(r.sticky_minutes, 0, 240, 10),
  };
}

// ─── comparisons ─────────────────────────────────────────────────────────────

const TAG_LABEL: Record<string, string> = {
  arabic_script: 'arabe',
  money: 'argent',
  angry: 'client fâché',
  low_res: 'photo floue/petite',
  photo: 'photo',
};
export const tagLabel = (tag: string, t: Tr = idTr) => (TAG_LABEL[tag] ? t(TAG_LABEL[tag]) : tag);

const ROUTE_LABEL = { easy: 'Haiku', complex: 'Sonnet', escalated: 'escalade' } as const;
export const routeBadge = (r: CompareCase['old_route']) => (r ? ROUTE_LABEL[r] : null);

/** Clicking the chosen verdict again clears it. */
export const nextVerdict = (current: CompareCase['verdict'], clicked: Exclude<CompareCase['verdict'], null>) =>
  current === clicked ? null : clicked;

export const MIXED = 'mixed';

/** A comparison side in words. */
export function hopLabel(hop: CompareHop, anthropic: Array<{ id: string; label: string }>, currentLabel?: string, t: Tr = idTr): string {
  if (hop.provider === 'anthropic') {
    if (hop.model === MIXED) return t('Claude mixte (Haiku/Sonnet)');
    return anthropic.find((m) => m.id === hop.model)?.label ?? `Claude — ${hop.model ?? '?'}`;
  }
  if (hop.model) return `OpenRouter — ${hop.model}`;
  return currentLabel ? t('OpenRouter — actuel ({m})', { m: currentLabel }) : t('OpenRouter — actuel');
}

export interface RunTotals {
  oldUsd: number; newUsd: number;
  oldMs: number | null; newMs: number | null;
  verdicts: { old: number; new: number; same: number; none: number };
  flagged: number;
}

const avg = (xs: number[]) => (xs.length ? Math.round(xs.reduce((s, x) => s + x, 0) / xs.length) : null);
const nums = (xs: Array<number | null>) => xs.filter((x): x is number => typeof x === 'number' && Number.isFinite(x));

export function runTotals(cases: CompareCase[]): RunTotals {
  const v = { old: 0, new: 0, same: 0, none: 0 };
  for (const c of cases) v[c.verdict ?? 'none'] += 1;
  return {
    oldUsd: nums(cases.map((c) => c.old_cost_usd)).reduce((s, x) => s + x, 0),
    newUsd: nums(cases.map((c) => c.new_cost_usd)).reduce((s, x) => s + x, 0),
    oldMs: avg(nums(cases.map((c) => c.old_ms))),
    newMs: avg(nums(cases.map((c) => c.new_ms))),
    verdicts: v,
    flagged: cases.filter((c) => c.flagged).length,
  };
}

export const runIsLive = (r: Pick<CompareRun, 'status'> | null | undefined) => r?.status === 'queued' || r?.status === 'running';

export function runProgress(r: Pick<CompareRun, 'done_count' | 'case_count' | 'spent_usd' | 'cap_usd'>): { done: number; spent: number } {
  const pct = (a: number, b: number) => (b > 0 ? Math.min(100, Math.max(0, (a / b) * 100)) : 0);
  return { done: pct(r.done_count, r.case_count), spent: pct(r.spent_usd, r.cap_usd) };
}

/** Clamp the case count the page sends (1..20). */
export const clampCases = (n: number) => (Number.isFinite(n) ? Math.min(20, Math.max(1, Math.round(n))) : 10);

/** A face box (fractions 0..1 of the photo) as an SVG rect in a `0 0 W H` viewBox, clipped to the photo. */
export function boxToRect(b: FaceBox, W: number, H: number): { x: number; y: number; width: number; height: number } {
  const c = (v: number) => Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
  const x0 = c(b.x); const y0 = c(b.y);
  const x1 = c(b.x + b.w); const y1 = c(b.y + b.h);
  return { x: x0 * W, y: y0 * H, width: Math.max(0, x1 - x0) * W, height: Math.max(0, y1 - y0) * H };
}

/** face_finder's input_meta, read defensively (the worker is the source of truth). */
export function faceMeta(meta: Record<string, unknown> | null): { imageUrl: string; width: number | null; height: number | null; oldBoxes: FaceBox[]; newBoxes: FaceBox[] } | null {
  if (!meta || typeof meta.imageUrl !== 'string') return null;
  const boxes = (v: unknown): FaceBox[] => (Array.isArray(v) ? v : [])
    .filter((b): b is FaceBox => !!b && typeof b === 'object' && ['x', 'y', 'w', 'h'].every((k) => typeof (b as Record<string, unknown>)[k] === 'number'));
  const dim = (v: unknown) => (typeof v === 'number' && v > 0 ? v : null);
  return { imageUrl: meta.imageUrl, width: dim(meta.width), height: dim(meta.height), oldBoxes: boxes(meta.oldBoxes), newBoxes: boxes(meta.newBoxes) };
}
